import React, { useState } from 'react';
import { Link } from 'expo-router';
import { LEARN_FAQS, LEARN_LESSONS, LEARN_STARTER_SLUGS } from '@/constants/learnCurriculum';
import { paths } from '@/content/learnHub.json';
import { useLearnProgress } from './LearnProgress';
import LearnDemo from './LearnDemo';
import LearnLessonLibrary from './LearnLessonLibrary';
import TutorialVideo from '@/components/Tutorials/TutorialVideo';
import { getLearnNextStop, getTutorialVideos } from '@/lib/tutorialVideos';

const [INTRO_VIDEO] = getTutorialVideos('learn', 'index');

export const STARTER_LESSONS = LEARN_STARTER_SLUGS;
const starterMinutes = LEARN_LESSONS.filter(lesson => STARTER_LESSONS.includes(lesson.slug)).reduce((sum, lesson) => sum + lesson.minutes, 0);

function LessonRow({ slug, label, index }) {
  const { completed } = useLearnProgress();
  const lesson = LEARN_LESSONS.find(item => item.slug === slug);
  return <Link href={`/docs/learn/${slug}`} className="learn-lesson-row">
    <span className={`learn-step ${completed[slug] ? 'is-complete' : ''}`} aria-label={completed[slug] ? 'Completed' : undefined}>{completed[slug] ? '✓' : String(index + 1).padStart(2, '0')}</span>
    <span>{label || lesson?.label}</span><span className="learn-row-time">{lesson?.minutes} min</span><span aria-hidden="true">↗</span>
  </Link>;
}

export default function LearnHub() {
  const { completed, lastLesson, saved, ready } = useLearnProgress();
  const [selected, setSelected] = useState('write');
  const path = paths.find(item => item.id === selected) || paths[0];
  const count = STARTER_LESSONS.filter(slug => completed[slug]).length;
  const next = STARTER_LESSONS.find(slug => !completed[slug]) || 'agentic-workflows';
  const resume = lastLesson && !completed[lastLesson] ? lastLesson : next;
  const started = !!lastLesson || Object.values(completed).some(Boolean);
  const introNext = getLearnNextStop(resume, { order: STARTER_LESSONS, includeCurrent: true });
  return <div className="learn-home">
    <section className="learn-hero">
      <h1>Learn how to use AI.</h1>
      <p className="learn-hero-copy">Free, beginner-friendly lessons and short video tutorials. Write better prompts, create images and video, and get real work done with AI.</p>
      <div className="learn-hero-actions"><Link className="learn-button" href={`/docs/learn/${resume}`}>{started ? 'Continue learning' : 'Start learning'} <span aria-hidden="true">→</span></Link><a className="learn-button learn-button-secondary" href="#learn-lessons">Browse all {LEARN_LESSONS.length} lessons <span aria-hidden="true">↓</span></a></div>
      {!!INTRO_VIDEO && <div className="learn-hero-video"><TutorialVideo video={INTRO_VIDEO} priority variant="hero" next={introNext} /></div>}
    </section>

    <LearnLessonLibrary />

    <section className="learn-start-strip" aria-label="Starter learning path">
      <div><h2>Your first five lessons.</h2><p>{starterMinutes} min reading · A practical place to start.</p></div>
      <div className="learn-start-strip-progress"><span>{count} of {STARTER_LESSONS.length} complete</span><progress aria-label="Starter path progress" value={count} max={STARTER_LESSONS.length} /></div>
      <Link href={`/docs/learn/${next}`} className="learn-text-button">{count === STARTER_LESSONS.length ? 'Keep learning' : count ? 'Continue the path' : 'Start here'} <span aria-hidden="true">→</span></Link>
    </section>
    <p className="learn-start-storage learn-fine">{!ready ? 'Loading your progress…' : saved ? 'Progress stays in this browser.' : 'Progress is available for this visit. Browser storage is unavailable.'}</p>

    <LearnDemo chooser />

    <section className="learn-section" aria-labelledby="paths-title">
      <div className="learn-section-heading"><div><p className="learn-eyebrow">Make it useful</p><h2 id="paths-title">What would you like to do?</h2></div><p>Pick a task. Find your next skill.</p></div>
      <div className="learn-paths">
        <div className="learn-path-picker" role="group" aria-label="Choose a learning path">{paths.map((item, index) => <button key={item.id} aria-pressed={selected === item.id} onClick={() => setSelected(item.id)}><span className="learn-path-number">0{index + 1}</span>{item.label}<span aria-hidden="true">→</span></button>)}</div>
        <div className="learn-path-detail" key={path.id}><p className="learn-eyebrow">{path.lessons.length} lessons · At your pace</p><h3>{path.title}</h3><p>{path.description}</p><div className="learn-path-example"><span className="learn-fine">Something you could try</span><p>“{path.prompt}”</p></div>{path.lessons.map((lesson, index) => <LessonRow key={lesson.slug} {...lesson} index={index} />)}</div>
      </div>
    </section>

    <section className="learn-section learn-faq" aria-labelledby="faq-title"><h2 id="faq-title">A few things to know.</h2>{LEARN_FAQS.map(item => <details key={item.q}><summary>{item.q}<span className="learn-disclosure" aria-hidden="true">+</span></summary><p>{item.a}</p></details>)}</section>
  </div>;
}
